import { useState, useEffect, useCallback, useMemo, useRef, type CSSProperties } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { SigningTokenInfo, SignatureField } from '@corner/shared';
import SignatureCapture from '../../components/app/SignatureCapture';
import PdfSigningViewer from '../../components/pdfViewer/PdfSigningViewer';
import { recipientViewFields } from '../../lib/signingRecipientView';
import { useAuthContext } from '../../contexts/AuthContext';
import { GuestSignatureModal, RecipientSignPathModal } from './RecipientSigningModals';

const API_BASE = import.meta.env.VITE_API_URL || '';
const F = 'Geist, system-ui, sans-serif';
const SERIF = '"Instrument Serif", system-ui, sans-serif';

function fieldTypeLabel(field: SignatureField): string {
  const labelLower = (field.label ?? '').toLowerCase();
  if (field.type === 'initials' || labelLower.includes('initial')) return 'Initials';
  if (field.type === 'date' || labelLower.includes('date')) return 'Date';
  if (
    field.type === 'text'
    || labelLower.includes('printed name')
    || labelLower.includes('full name')
    || /\btitle\b/.test(labelLower)
    || /\bname\b/.test(labelLower)
  ) {
    if (/\btitle\b/.test(labelLower)) return 'Title';
    if (/\bname\b|printed\s*name|full\s*name/.test(labelLower)) return 'Name';
    return 'Text';
  }
  return 'Signature';
}

function fieldHeading(field: SignatureField): string {
  const t = field.label?.trim();
  if (t) return t;
  return fieldTypeLabel(field);
}

const EXPLORE_TEXT_INPUT: CSSProperties = {
  width: '100%', border: '1px solid #E8DED4', borderRadius: 8, padding: '10px 12px',
  fontSize: 14, fontFamily: F, color: '#3D3229', backgroundColor: '#FFFFFF', boxSizing: 'border-box',
};

function SignCelebrationModal({
  open,
  senderName,
  recipientName,
  onSend,
}: {
  open: boolean;
  senderName: string;
  recipientName: string;
  onSend: () => void;
}) {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        backgroundColor: 'rgba(29, 24, 20, 0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="celebration-title"
    >
      <div style={{
        width: '100%', maxWidth: 440, backgroundColor: '#FFFFFF', borderRadius: 16,
        padding: '36px 32px 28px', boxShadow: '0 12px 48px rgba(0,0,0,0.18)',
        border: '1px solid #E8DED4',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: 28, backgroundColor: '#FDF5EF',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20,
        }}>
          <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
            <path d="M6 14l6 6L22 8" stroke="#D97645" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h2 id="celebration-title" style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 400, color: '#3D3229', fontFamily: SERIF }}>
          Congratulations
        </h2>
        <p style={{ margin: '0 0 16px', fontSize: 15, color: '#6B5E52', lineHeight: 1.55 }}>
          You&apos;ve taken an important step — your signature and details are recorded on this document.
        </p>
        <p style={{ margin: '0 0 28px', fontSize: 14, color: '#3D3229', lineHeight: 1.5 }}>
          Your part is complete. <strong style={{ fontWeight: 600 }}>{senderName}</strong> gets an email that <strong style={{ fontWeight: 600 }}>{recipientName}</strong> has signed.
          Tap <strong style={{ fontWeight: 600 }}>Send</strong> to close this message and view your confirmation.
        </p>
        <button
          type="button"
          onClick={onSend}
          style={{
            width: '100%', border: 'none', borderRadius: 8, padding: '14px 20px',
            backgroundColor: '#D97645', color: '#FFFFFF', fontSize: 15, fontWeight: 600,
            cursor: 'pointer', fontFamily: F,
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
}

// ─── Step type ──────────────────────────────────────────────────────
type Phase = 'explore' | 'complete';

export default function SigningWorkspacePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { getToken, user } = useAuthContext();

  // ── Core state ──────────────────────────────────────────────────
  const [info, setInfo] = useState<SigningTokenInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<Phase>('explore');
  /** Guest-only captured signature (logged-in users may use accountSig instead). */
  const [guestSignature, setGuestSignature] = useState<string | null>(null);
  const [accountSig, setAccountSig] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState('');
  const [allComplete, setAllComplete] = useState(false);
  const [confirmingSign, setConfirmingSign] = useState(false);
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatEnabled, setChatEnabled] = useState<boolean | null>(null); // null = loading
  const [choiceModalOpen, setChoiceModalOpen] = useState(false);
  const [guestSigModalOpen, setGuestSigModalOpen] = useState(false);
  const [chatSelectionDraft, setChatSelectionDraft] = useState<{ text: string; page: number } | null>(null);
  /** Shown on signed PDF / audit context; prefilled from invite, editable before signing. */
  const [recipientPrintedName, setRecipientPrintedName] = useState('');
  const [recipientTitle, setRecipientTitle] = useState('');
  const [recipientSignDate, setRecipientSignDate] = useState('');
  const recipientSignDateFrozenRef = useRef(false);
  const [emphasizeSignerName, setEmphasizeSignerName] = useState(false);

  // ── Token validation on mount ──────────────────────────────────
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/signing/token/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          if (errData.code === 'TOKEN_EXPIRED') {
            navigate(`/sign/${token}/error?type=expired`, { replace: true });
            return null;
          }
          if (errData.code === 'TOKEN_REVOKED') {
            navigate(`/sign/${token}/error?type=revoked`, { replace: true });
            return null;
          }
          if (errData.code === 'EMAIL_VERIFICATION_REQUIRED') {
            navigate(`/sign/${token}`, { replace: true });
            return null;
          }
          throw new Error('Invalid token');
        }
        return res.json();
      })
      .then((data: SigningTokenInfo | null) => {
        if (!data) return;
        if (data.signerStatus === 'signed') {
          navigate(`/sign/${token}/complete`, { replace: true });
          return;
        }
        setInfo(data);
      })
      .catch(() => navigate(`/sign/${token}/error?type=not_found`, { replace: true }))
      .finally(() => setLoading(false));
  }, [token, navigate]);

  // ── Chat feature check ─────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/signing/token/${token}/chat/quota`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => { setChatEnabled(data?.enabled === true && data?.remaining > 0); })
      .catch(() => { setChatEnabled(false); });
  }, [token]);

  // ── Load saved signature for logged-in recipients ───────────────
  useEffect(() => {
    const t = getToken();
    if (!t || !user?.signatureId) {
      setAccountSig(null);
      return;
    }
    let cancelled = false;
    const u = `${API_BASE}/api/auth/me/signature-preview?type=signature&t=${encodeURIComponent(user.updatedAt ?? user.signatureId)}`;
    fetch(u, { headers: { Authorization: `Bearer ${t}` } })
      .then((res) => {
        if (!res.ok) throw new Error('preview');
        return res.blob();
      })
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result as string);
            fr.onerror = reject;
            fr.readAsDataURL(blob);
          }),
      )
      .then((dataUrl) => {
        if (!cancelled) setAccountSig(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setAccountSig(null);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, user?.signatureId, user?.updatedAt]);

  // ── Prefill signer details once token info loads ────────────────
  useEffect(() => {
    if (!info) return;
    if (!recipientSignDateFrozenRef.current) {
      setRecipientSignDate(
        new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
      );
      recipientSignDateFrozenRef.current = true;
    }
    setRecipientPrintedName((prev) => (prev === '' ? (info.signerName ?? '').trim() : prev));
  }, [info]);

  useEffect(() => {
    if (recipientPrintedName.trim()) setEmphasizeSignerName(false);
  }, [recipientPrintedName]);

  const signingDataUrl = guestSignature ?? accountSig;

  // ── Derived ────────────────────────────────────────────────────
  const yourFields = useMemo(
    () => recipientViewFields(info?.fields, info?.pageCount),
    [info?.fields, info?.pageCount],
  );
  const sourceDocUrl = token ? `${API_BASE}/api/signing/token/${token}/source-document` : null;

  const handleSignatureCreated = useCallback((dataUrl: string) => {
    setGuestSignature(dataUrl);
  }, []);

  const handleConfirmAndSign = useCallback(async () => {
    if (!signingDataUrl || !token || !info) return;
    const name = recipientPrintedName.trim();
    if (!name) return;

    setConfirmingSign(true);
    setSubmitError('');

    try {
      const res = await fetch(`${API_BASE}/api/signing/token/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signatureDataUrl: signingDataUrl,
          printedName: name,
          printedTitle: recipientTitle.trim(),
          signDateText: recipientSignDate.trim(),
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        if (errData.code === 'TOKEN_EXPIRED') {
          navigate(`/sign/${token}/error?type=expired`, { replace: true });
          return;
        }
        if (errData.code === 'TOKEN_REVOKED') {
          navigate(`/sign/${token}/error?type=revoked`, { replace: true });
          return;
        }
        if (errData.code === 'EMAIL_VERIFICATION_REQUIRED') {
          navigate(`/sign/${token}`, { replace: true });
          return;
        }
        throw new Error('Sign failed');
      }
      const data = await res.json();

      // Immediately reflect the completed markers in the viewer so the signer
      // sees the successful placements (green state) before the celebration modal.
      const signedAtNow = Date.now();
      setInfo((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          fields: prev.fields.map((field) => (
            field.placed
              ? field
              : {
                  ...field,
                  placed: true,
                  signedAt: field.signedAt ?? signedAtNow,
                  signedByEmail: field.signedByEmail ?? prev.signerEmail,
                  state: 'signed',
                }
          )),
        };
      });

      const freshRes = await fetch(`${API_BASE}/api/signing/token/${token}`);
      if (freshRes.ok) {
        const fresh: SigningTokenInfo = await freshRes.json();
        // Never regress visual completion state if the follow-up token read
        // races and returns stale field flags.
        setInfo((prev) => {
          if (!prev) return fresh;
          const prevById = new Map(prev.fields.map((f) => [f.id, f]));
          return {
            ...fresh,
            fields: fresh.fields.map((f) => {
              const old = prevById.get(f.id);
              if (!old) return f;
              const wasSigned = !!(old.placed || old.signedAt || old.state === 'signed');
              const nowSigned = !!(f.placed || f.signedAt || f.state === 'signed');
              if (!wasSigned || nowSigned) return f;
              return {
                ...f,
                placed: true,
                signedAt: old.signedAt ?? Date.now(),
                signedByEmail: old.signedByEmail ?? prev.signerEmail,
                state: 'signed',
              };
            }),
          };
        });
      }

      setAllComplete(!!data.allComplete);
      await new Promise((resolve) => setTimeout(resolve, 650));
      setCelebrationOpen(true);
    } catch {
      setSubmitError('Something went wrong while signing. Please check your connection and try again.');
    } finally {
      setConfirmingSign(false);
    }
  }, [
    signingDataUrl,
    token,
    info,
    recipientPrintedName,
    recipientTitle,
    recipientSignDate,
    navigate,
  ]);

  const openAuthRegisterForSigning = useCallback(() => {
    if (!token) return;
    const returnTo = `/sign/${token}/workspace`;
    const setupUrl = `/auth/signature-setup?returnTo=${encodeURIComponent(returnTo)}`;
    navigate(`/auth/register?redirect=${encodeURIComponent(setupUrl)}`);
  }, [navigate, token]);

  const openAuthLoginForSigning = useCallback(() => {
    if (!token) return;
    const returnTo = `/sign/${token}/workspace`;
    navigate(`/auth/login?redirect=${encodeURIComponent(returnTo)}`);
  }, [navigate, token]);

  const handleViewerFieldClick = useCallback(
    (fieldId: string) => {
      if (!info) return;
      const idx = yourFields.findIndex((f) => f.id === fieldId);
      if (idx < 0) return;
      const field = yourFields[idx];
      if (field.placed) return;
      if (!signingDataUrl) {
        setChoiceModalOpen(true);
        return;
      }
      if (!recipientPrintedName.trim()) {
        setEmphasizeSignerName(true);
        return;
      }
      void handleConfirmAndSign();
    },
    [info, yourFields, signingDataUrl, recipientPrintedName, handleConfirmAndSign],
  );

  const handleChooseGuest = useCallback(() => {
    setChoiceModalOpen(false);
    setGuestSigModalOpen(true);
  }, []);

  const handleGuestSignatureApply = useCallback((dataUrl: string) => {
    setGuestSignature(dataUrl);
    setGuestSigModalOpen(false);
  }, []);

  const handleChooseCorner = useCallback(() => {
    setChoiceModalOpen(false);
    openAuthRegisterForSigning();
  }, [openAuthRegisterForSigning]);

  const handleChooseSignIn = useCallback(() => {
    setChoiceModalOpen(false);
    openAuthLoginForSigning();
  }, [openAuthLoginForSigning]);

  const handleSigningPdfAskCorner = useCallback((snippet: { text: string; page: number }) => {
    setChatOpen(true);
    setChatSelectionDraft(snippet);
  }, []);

  const clearChatSelectionDraft = useCallback(() => setChatSelectionDraft(null), []);

  const handleChangeGuestSignature = useCallback(() => {
    setGuestSignature(null);
    setChoiceModalOpen(true);
  }, []);

  const handleCelebrationSend = useCallback(() => {
    setCelebrationOpen(false);
    setPhase('complete');
  }, []);

  // ── Loading ────────────────────────────────────────────────────
  if (loading || !info) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F2ED', fontFamily: F }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 32, height: 32, border: '2px solid #E8DED4', borderTopColor: '#D97645', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ margin: 0, fontSize: 14, color: '#9E9189' }}>Loading...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh', minHeight: 0, display: 'flex', flexDirection: 'column',
      backgroundColor: '#F5F2ED', fontFamily: F, overflow: 'hidden',
    }}>
      {/* ── Top bar ───────────────────────────────────────────── */}
      <TopBar info={info} phase={phase} totalFields={yourFields.length} />

      {phase === 'explore' && (
        <ExploreStep
          info={info}
          yourFields={yourFields}
          signingDataUrl={signingDataUrl}
          sourceDocUrl={sourceDocUrl}
          onFieldClick={handleViewerFieldClick}
          onCreateAccount={openAuthRegisterForSigning}
          onSignIn={openAuthLoginForSigning}
          onContinueAsGuest={() => setGuestSigModalOpen(true)}
          onOpenCompareModal={() => setChoiceModalOpen(true)}
          onContinuePrepared={() => { void handleConfirmAndSign(); }}
          signSubmitting={confirmingSign}
          signSubmitError={submitError}
          onSignatureCreated={handleSignatureCreated}
          onChangeGuestSignature={handleChangeGuestSignature}
          guestSignature={guestSignature}
          signingPdfAskCorner={chatEnabled !== false ? handleSigningPdfAskCorner : undefined}
          recipientPrintedName={recipientPrintedName}
          onRecipientPrintedNameChange={setRecipientPrintedName}
          recipientTitle={recipientTitle}
          onRecipientTitleChange={setRecipientTitle}
          recipientSignDate={recipientSignDate}
          onRecipientSignDateChange={setRecipientSignDate}
          emphasizeSignerName={emphasizeSignerName}
        />
      )}

      {phase !== 'complete' && token && (chatEnabled === true || chatOpen) && (
        <button
          onClick={() => setChatOpen((o) => !o)}
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 100,
            width: 48, height: 48, borderRadius: 24,
            backgroundColor: chatOpen ? '#3D3229' : '#D97645', border: 'none',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          title="Ask about this document"
        >
          {chatOpen ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
          )}
        </button>
      )}

      {/* Chat drawer */}
      {chatOpen && token && (
        <SigningChatDrawer
          token={token}
          documentName={info.documentName}
          onClose={() => setChatOpen(false)}
          selectionDraft={chatSelectionDraft}
          onSelectionDraftConsumed={clearChatSelectionDraft}
        />
      )}

      {phase === 'complete' && (
        <CompleteStep
          info={info}
          yourFields={yourFields}
          allComplete={allComplete}
          token={token!}
          recipientPrintedName={recipientPrintedName}
          recipientTitle={recipientTitle}
          recipientSignDate={recipientSignDate}
          signingDataUrl={signingDataUrl}
        />
      )}

      <RecipientSignPathModal
        open={choiceModalOpen}
        senderName={info.senderName}
        onClose={() => setChoiceModalOpen(false)}
        onChooseGuest={handleChooseGuest}
        onChooseCorner={handleChooseCorner}
        onChooseSignIn={handleChooseSignIn}
      />
      <GuestSignatureModal
        open={guestSigModalOpen}
        documentName={info.documentName}
        onClose={() => setGuestSigModalOpen(false)}
        onApply={handleGuestSignatureApply}
      />
      <SignCelebrationModal
        open={celebrationOpen}
        senderName={info.senderName}
        recipientName={recipientPrintedName.trim() || info.signerName}
        onSend={handleCelebrationSend}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ── Top Bar ──────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

function TopBar({ info, phase, totalFields }: {
  info: SigningTokenInfo; phase: Phase; totalFields: number;
}) {
  const stepLabels: Record<Phase, string> = {
    explore: totalFields === 0 ? 'Review document' : `Review document · ${totalFields} field${totalFields !== 1 ? 's' : ''}`,
    complete: 'Complete',
  };

  return (
    <div style={{
      height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 24px', backgroundColor: '#FFFFFF', borderBottom: '1px solid #E8DED4', flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <svg width="22" height="22" viewBox="0 0 256 256" fill="none" style={{ flexShrink: 0 }}>
            <defs><linearGradient id="cLogo" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse"><stop stopColor="#D46A3A"/><stop offset="1" stopColor="#8B3A10"/></linearGradient></defs>
            <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#cLogo)" strokeWidth="40" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#3D3229' }}>Corner</span>
        </div>
        <div style={{ width: 1, height: 20, backgroundColor: '#E8DED4', flexShrink: 0 }} />
        {/* Step label */}
        <span style={{ fontSize: 13, color: '#6B5E52' }}>{stepLabels[phase]}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* Sender badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%', backgroundColor: '#D97645',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <span style={{ fontSize: 9, fontWeight: 600, color: '#FFFFFF' }}>
              {info.senderName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
            </span>
          </div>
          <span style={{ fontSize: 12, color: '#6B5E52' }}>From {info.senderName}</span>
        </div>
        {/* Secure badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width="11" height="11" viewBox="0 0 10 10" fill="none">
            <path d="M5 1L2 2.5v2.5c0 2 1.3 3.5 3 4 1.7-.5 3-2 3-4V2.5L5 1z" stroke="#1B7A4B" strokeWidth="0.8" />
          </svg>
          <span style={{ fontSize: 11, fontWeight: 500, color: '#1B7A4B' }}>Secure</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ── Explore: document first (no account required) ────────────────
// ═══════════════════════════════════════════════════════════════════

function ExploreStep({
  info,
  yourFields,
  signingDataUrl,
  sourceDocUrl,
  onFieldClick,
  onCreateAccount,
  onSignIn,
  onContinueAsGuest,
  onOpenCompareModal,
  onContinuePrepared,
  onSignatureCreated,
  onChangeGuestSignature,
  guestSignature,
  signingPdfAskCorner,
  recipientPrintedName,
  onRecipientPrintedNameChange,
  recipientTitle,
  onRecipientTitleChange,
  recipientSignDate,
  onRecipientSignDateChange,
  emphasizeSignerName,
  signSubmitting,
  signSubmitError,
}: {
  info: SigningTokenInfo;
  yourFields: SignatureField[];
  signingDataUrl: string | null;
  sourceDocUrl: string | null;
  onFieldClick: (fieldId: string) => void;
  onCreateAccount: () => void;
  onSignIn: () => void;
  onContinueAsGuest: () => void;
  /** Full comparison modal (same options as sidebar, with extra detail). */
  onOpenCompareModal: () => void;
  onContinuePrepared: () => void;
  onSignatureCreated: (dataUrl: string) => void;
  onChangeGuestSignature: () => void;
  guestSignature: string | null;
  signingPdfAskCorner?: (snippet: { text: string; page: number }) => void;
  recipientPrintedName: string;
  onRecipientPrintedNameChange: (v: string) => void;
  recipientTitle: string;
  onRecipientTitleChange: (v: string) => void;
  recipientSignDate: string;
  onRecipientSignDateChange: (v: string) => void;
  emphasizeSignerName?: boolean;
  signSubmitting: boolean;
  signSubmitError: string;
}) {
  const firstUnsigned = yourFields.findIndex((f) => !f.placed);
  const initialPage = firstUnsigned >= 0 ? yourFields[firstUnsigned].page : 1;
  const unsignedCount = yourFields.filter((f) => !f.placed).length;
  const noAssignedFields = yourFields.length === 0;

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
      <div style={{
        flex: 1, minHeight: 0, backgroundColor: '#E8E4DE', display: 'flex', flexDirection: 'column',
        alignItems: 'center', overflow: 'auto', padding: '24px 40px', gap: 12,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
          borderRadius: 999, backgroundColor: '#F7F3EE',
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#D97645', flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontWeight: 500, color: '#3D3229' }}>
            {noAssignedFields
              ? `Review and sign · ${info.documentName}`
              : `${unsignedCount} field${unsignedCount !== 1 ? 's' : ''} to complete · ${info.documentName}`}
          </span>
        </div>

        <div style={{
          position: 'relative', width: '100%', maxWidth: 1100, flex: 1, minHeight: 600,
          backgroundColor: '#FFFFFF', borderRadius: 12, overflow: 'hidden', flexShrink: 0,
          boxShadow: '#00000014 0px 2px 16px',
        }}>
          {sourceDocUrl ? (
            <PdfSigningViewer
              pdfUrl={sourceDocUrl}
              fields={yourFields}
              activeFieldId={null}
              onFieldClick={onFieldClick}
              currentSignerId={info.signerEmail}
              mode="sign"
              signerName={info.signerName}
              signaturePreview={signingDataUrl ?? undefined}
              showThumbnails={true}
              initialPage={initialPage}
              onAskCornerAboutSelection={signingPdfAskCorner}
            />
          ) : null}

          <div style={{
            position: 'absolute', top: 16, right: 16, maxWidth: 280,
            display: 'flex', flexDirection: 'column', gap: 4,
            padding: '12px 14px', borderRadius: 10,
            backgroundColor: 'rgba(253, 248, 244, 0.96)', border: '1px solid #F0DFD0',
            boxShadow: '#00000012 0px 8px 24px', backdropFilter: 'blur(8px)',
            pointerEvents: 'none',
          }}>
            <span style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#D97645', fontWeight: 600 }}>
              {noAssignedFields ? 'Signing' : 'Your fields'}
            </span>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#3D3229' }}>
              {noAssignedFields
                ? 'No exact spots were marked for you on this document. Fill in your details and signature on the right, then confirm once to finish.'
                : 'Markers show where you sign, your printed name, title, and date. Use Confirm & sign on the right — or click any marker when you\'re ready to submit all fields at once.'}
            </span>
          </div>
        </div>
      </div>

      <div style={{
        width: 360, borderLeft: '1px solid #E8DED4', backgroundColor: '#FFFFFF',
        display: 'flex', flexDirection: 'column', flexShrink: 0, minHeight: 0,
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid #E8DED4',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#3D3229' }}>
            Review & sign
          </span>
        </div>

        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto', padding: '24px 24px',
          display: 'flex', flexDirection: 'column', gap: 20,
        }}>
          <p style={{ margin: 0, fontSize: 14, color: '#6B5E52', lineHeight: '22px' }}>
            Read <strong style={{ fontWeight: 600, color: '#3D3229' }}>{info.documentName}</strong>, complete your details and signature on the right, then confirm once to apply every marker (signature, date, name lines, and title) in the right places. Use a free Corner account for a saved signature, or continue as a guest for this envelope only.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              type="button"
              onClick={onCreateAccount}
              style={{
                width: '100%', border: 'none', borderRadius: 8, padding: '12px 16px',
                backgroundColor: '#2E2318', color: '#FDFCFB', fontSize: 14, fontWeight: 500,
                cursor: 'pointer', fontFamily: F,
              }}
            >
              Create free account
            </button>
            <button
              type="button"
              onClick={onSignIn}
              style={{
                width: '100%', border: '1px solid #E8DED4', borderRadius: 8, padding: '11px 16px',
                backgroundColor: '#FFFFFF', color: '#3D3229', fontSize: 13, fontWeight: 500,
                cursor: 'pointer', fontFamily: F,
              }}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={onContinueAsGuest}
              style={{
                width: '100%', border: 'none', background: 'none', padding: '6px 8px',
                color: '#D97645', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: F,
                textDecoration: 'underline', textUnderlineOffset: 3,
              }}
            >
              Continue as guest — this document only
            </button>
          </div>

          <button
            type="button"
            onClick={onOpenCompareModal}
            style={{
              alignSelf: 'flex-start', border: 'none', background: 'none', padding: 0,
              color: '#8B7D6B', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: F,
              textDecoration: 'underline', textUnderlineOffset: 3,
            }}
          >
            Compare Corner account vs guest
          </button>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9E9189' }}>
              Your details
            </span>
            <p style={{ margin: 0, fontSize: 12, color: '#8B7D6B', lineHeight: '18px' }}>
              Name is prefilled from this invite when available. The date is set to when you opened this document (you can edit it if needed).
            </p>
            {emphasizeSignerName ? (
              <p style={{ margin: 0, fontSize: 12, color: '#B42318', lineHeight: '18px' }}>
                Add your name here first, then use Confirm & sign (or click a marker to submit).
              </p>
            ) : null}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#6B5E52' }}>Name</span>
              <input
                type="text"
                autoComplete="name"
                value={recipientPrintedName}
                onChange={(e) => onRecipientPrintedNameChange(e.target.value)}
                placeholder="Printed name"
                style={{
                  ...EXPLORE_TEXT_INPUT,
                  borderColor: emphasizeSignerName ? '#B42318' : '#E8DED4',
                  boxShadow: emphasizeSignerName ? '0 0 0 1px #B42318' : undefined,
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#6B5E52' }}>Title</span>
              <input
                type="text"
                value={recipientTitle}
                onChange={(e) => onRecipientTitleChange(e.target.value)}
                placeholder="e.g. Managing Partner"
                style={EXPLORE_TEXT_INPUT}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#6B5E52' }}>Date</span>
              <input
                type="text"
                value={recipientSignDate}
                onChange={(e) => onRecipientSignDateChange(e.target.value)}
                placeholder="MM/DD/YYYY"
                style={EXPLORE_TEXT_INPUT}
              />
            </label>
          </div>

          {!signingDataUrl && (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 14px',
              backgroundColor: '#F9F6F2', border: '1px solid #E8DED4', borderRadius: 10,
            }}>
              <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9E9189' }}>
                Or prepare signature here
              </span>
              <SignatureCapture onCapture={onSignatureCreated} />
              <p style={{ margin: 0, fontSize: 11, color: '#8B7D6B', lineHeight: '16px' }}>
                Add your signature, then use Confirm & sign — your inputs will fill every marker type on the PDF.
              </p>
            </div>
          )}

          {signingDataUrl && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9E9189' }}>
                  Your signature
                </span>
                <div style={{
                  border: '1px solid #E8DED4', borderRadius: 10, padding: 16,
                  backgroundColor: '#FAFAF8', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  minHeight: 72,
                }}>
                  <img src={signingDataUrl} alt="Signature" style={{ maxWidth: '80%', maxHeight: 60, objectFit: 'contain' }} />
                </div>
              </div>
              {guestSignature && (
                <button
                  type="button"
                  onClick={onChangeGuestSignature}
                  style={{
                    alignSelf: 'flex-start', border: 'none', background: 'none',
                    color: '#D97645', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: F,
                    textDecoration: 'underline', textUnderlineOffset: 3,
                  }}
                >
                  Change guest signature
                </button>
              )}
              <button
                type="button"
                onClick={onContinuePrepared}
                disabled={!recipientPrintedName.trim() || signSubmitting}
                style={{
                  width: '100%', border: 'none', borderRadius: 8, padding: '13px 20px',
                  backgroundColor: !recipientPrintedName.trim() || signSubmitting ? '#C4B8AE' : '#2E2318', color: '#FDFCFB',
                  fontSize: 14, fontWeight: 500, cursor: !recipientPrintedName.trim() || signSubmitting ? 'default' : 'pointer', fontFamily: F,
                }}
              >
                {signSubmitting ? 'Signing…' : 'Confirm & sign'}
              </button>
              {signSubmitError ? (
                <p style={{ margin: 0, fontSize: 12, color: '#B42318', lineHeight: '16px', textAlign: 'center' }}>
                  {signSubmitError}
                </p>
              ) : null}
              {!recipientPrintedName.trim() ? (
                <p style={{ margin: 0, fontSize: 11, color: '#B42318', lineHeight: '16px', textAlign: 'center' }}>
                  Add your name above to continue.
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ── Step 3: Completion ───────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

function CompleteStep({ info, yourFields, allComplete, token, recipientPrintedName, recipientTitle, recipientSignDate, signingDataUrl }: {
  info: SigningTokenInfo;
  yourFields: SignatureField[];
  allComplete: boolean;
  token: string;
  recipientPrintedName: string;
  recipientTitle: string;
  recipientSignDate: string;
  signingDataUrl: string | null;
}) {
  const [downloading, setDownloading] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const displayName = recipientPrintedName.trim() || info.signerName;
  const displayTitle = recipientTitle.trim() || 'Not provided';
  const displayDate = recipientSignDate.trim() || 'Not provided';

  const pageList = useMemo(() => {
    const pages = [...new Set(yourFields.map((f) => f.page))].sort((a, b) => a - b);
    return pages.length > 0 ? pages.map((p) => `Page ${p}`).join(', ') : 'No marked pages';
  }, [yourFields]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/api/signing/token/${token}/download`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signed_${info.documentName}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadDocx = async () => {
    setDownloadingDocx(true);
    try {
      const res = await fetch(`${API_BASE}/api/signing/token/${token}/download-docx`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const base = info.documentName.replace(/\.(pdf|docx)$/i, '').trim() || 'document';
      a.download = `signed_${base}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingDocx(false);
    }
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflow: 'auto' }}>
      <div style={{
        width: 480, backgroundColor: '#FFFFFF', borderRadius: 16,
        padding: '48px 40px 40px', boxShadow: '0px 4px 24px #0000000F',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28,
      }}>
        {/* Success icon */}
        <div style={{
          width: 64, height: 64, borderRadius: 32, backgroundColor: '#E8F5ED',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path d="M6 14l6 6L22 8" stroke="#1B7A4B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Text */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 400, color: '#3D3229', fontFamily: SERIF }}>
            You&apos;re all set
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: '#6B5E52', textAlign: 'center', lineHeight: '21px' }}>
            {allComplete
              ? `Congratulations — you've finished signing. We'll let ${info.senderName} know. Sit tight; they may still need to wrap up on their side.`
              : `Thank you — your part is done. We'll notify ${info.senderName} that you've signed. If someone else still needs to sign, they'll finish next.`}
          </p>
        </div>

        {/* Recorded signer details */}
        <div style={{ width: '100%', border: '1px solid #E8DED4', borderRadius: 12, padding: 14, backgroundColor: '#FAFAF8', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9E9189' }}>
            Recorded signer details
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
            <div style={{ fontSize: 13, color: '#3D3229' }}><strong style={{ fontWeight: 600 }}>Pages:</strong> {pageList}</div>
            <div style={{ fontSize: 13, color: '#3D3229' }}><strong style={{ fontWeight: 600 }}>Name:</strong> {displayName}</div>
            <div style={{ fontSize: 13, color: '#3D3229' }}><strong style={{ fontWeight: 600 }}>Title:</strong> {displayTitle}</div>
            <div style={{ fontSize: 13, color: '#3D3229' }}><strong style={{ fontWeight: 600 }}>Date:</strong> {displayDate}</div>
            <div style={{ fontSize: 13, color: '#3D3229', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <strong style={{ fontWeight: 600 }}>Signature:</strong>
              <div style={{ border: '1px solid #E8DED4', borderRadius: 10, backgroundColor: '#FFFFFF', minHeight: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
                {signingDataUrl ? (
                  <img src={signingDataUrl} alt="Recorded signature" style={{ maxWidth: '90%', maxHeight: 56, objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: 12, color: '#9E9189' }}>Signature recorded</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Field summary */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {yourFields.map((f, i) => (
            <div
              key={f.id ?? i}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                borderRadius: 8, backgroundColor: '#F4FAF6',
              }}
            >
              <div style={{
                width: 20, height: 20, borderRadius: 10, backgroundColor: '#1B7A4B',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5l2.5 2.5L8 3" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 400, color: '#3D3229' }}>{fieldHeading(f)}</span>
                <span style={{ fontSize: 11, color: '#9E9189' }}>
                  Page {f.page}{f.signedAt ? ` · ${new Date(f.signedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}
                </span>
                <span style={{ fontSize: 11, color: '#6B5E52' }}>
                  {(() => {
                    const type = fieldTypeLabel(f).toLowerCase();
                    if (type === 'signature') return 'Applied: Signature on file';
                    if (type === 'name') return `Applied: ${displayName}`;
                    if (type === 'title') return `Applied: ${displayTitle}`;
                    if (type === 'date') return `Applied: ${displayDate}`;
                    return 'Applied';
                  })()}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Download — only when all parties have signed */}
          {allComplete && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              style={{
                width: '100%', border: 'none', borderRadius: 8, padding: '13px 20px',
                backgroundColor: '#2E2318', color: '#FDFCFB',
                fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: F,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 2v7m0 0L4.5 6.5M7 9l2.5-2.5M2 11h10" stroke="#FDFCFB" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {downloading ? 'Downloading...' : 'Download Signed Copy'}
            </button>
          )}

          {allComplete && (
            <>
              <button
                type="button"
                onClick={handleDownloadDocx}
                disabled={downloadingDocx}
                style={{
                  width: '100%', border: '1px solid #E8DED4', borderRadius: 8, padding: '11px 20px',
                  backgroundColor: '#FAFAF8', color: '#3D3229',
                  fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: F,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {downloadingDocx ? 'Preparing Word…' : 'Download Word (.docx)'}
              </button>
              <p style={{ margin: 0, fontSize: 11, lineHeight: '16px', color: '#9E9189', textAlign: 'center' }}>
                Editable text from the signed PDF — formatting may differ from the PDF.
              </p>
            </>
          )}

          {/* Account CTA placeholder — secondary, non-blocking */}
          <button
            onClick={() => { window.open('/', '_blank'); }}
            style={{
              width: '100%', border: '1px solid #E8DED4', borderRadius: 8, padding: '11px 20px',
              backgroundColor: 'transparent', color: '#3D3229',
              fontSize: 13, fontWeight: 400, cursor: 'pointer', fontFamily: F,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            Learn more about Corner
          </button>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, paddingTop: 4 }}>
          {allComplete && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 16, height: 16, borderRadius: 8, backgroundColor: '#1B7A4B',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <path d="M1.5 4l1.5 1.5L6.5 2.5" stroke="#FFFFFF" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span style={{ fontSize: 12, fontWeight: 400, color: '#3D3229' }}>All parties have signed</span>
            </div>
          )}
          <span style={{ fontSize: 12, fontWeight: 400, color: '#B5AA9C' }}>
            You can safely close this window
          </span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ── Guest Signing Chat Drawer ────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
}

function SigningChatDrawer({ token, documentName, onClose, selectionDraft, onSelectionDraftConsumed }: {
  token: string; documentName: string; onClose: () => void;
  selectionDraft?: { text: string; page: number } | null;
  onSelectionDraftConsumed?: () => void;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!selectionDraft?.text?.trim()) return;
    const raw = selectionDraft.text.trim().replace(/\s+/g, ' ');
    const maxQuote = 220;
    const quote = raw.length > maxQuote ? `${raw.slice(0, maxQuote)}…` : raw;
    const pageNote = selectionDraft.page != null ? `[Page ${selectionDraft.page}] ` : '';
    const block = `${pageNote}"${quote}" — `;
    setInput((prev) => {
       const next = prev.trim() ? `${prev}\n${block}` : block;
       return next.length > 500 ? block.slice(0, 500) : next;
     });
    onSelectionDraftConsumed?.();
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [selectionDraft, onSelectionDraftConsumed]);

  // Fetch initial quota + history
  useEffect(() => {
    fetch(`${API_BASE}/api/signing/token/${token}/chat/quota`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data) {
          setRemaining(data.remaining);
          if (!data.enabled || data.remaining <= 0) setQuotaExhausted(true);
          // Hydrate from server history
          if (data.recentMessages?.length > 0) {
            setMessages(data.recentMessages.map((m: any) => ({
              role: m.role === 'assistant' ? 'ai' as const : 'user' as const,
              text: m.content,
            })));
          }
        }
      })
      .catch(() => {});
  }, [token]);

  const isLowQuota = remaining != null && remaining === 1 && !quotaExhausted;

  const handleSend = useCallback(async () => {
    const q = input.trim();
    if (!q || sending || quotaExhausted) return;
    setInput('');
    setError('');
    setMessages((prev) => [...prev, { role: 'user', text: q }]);
    setSending(true);

    try {
      const res = await fetch(`${API_BASE}/api/signing/token/${token}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.code === 'SIGNING_CHAT_QUOTA_EXCEEDED') {
          setQuotaExhausted(true);
          setRemaining(0);
          return;
        }
        throw new Error(data.error || 'Failed');
      }

      const data = await res.json();
      setMessages((prev) => [...prev, { role: 'ai', text: data.answer }]);
      setRemaining(data.remainingQuota);
      if (data.remainingQuota <= 0) setQuotaExhausted(true);
    } catch {
      setError('Could not get an explanation right now. Please try again in a moment.');
    } finally {
      setSending(false);
    }
  }, [input, sending, quotaExhausted, token]);

  return (
    <div style={{
      position: 'fixed', bottom: 80, right: 24, zIndex: 99,
      width: 360, maxHeight: 480, backgroundColor: '#FFFFFF',
      borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
      display: 'flex', flexDirection: 'column', fontFamily: F,
      border: '1px solid #E8DED4', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 18px', borderBottom: '1px solid #E8DED4',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#3D3229' }}>Ask about this document</span>
          <span style={{ fontSize: 11, color: isLowQuota ? '#D97645' : '#9E9189', fontWeight: isLowQuota ? 500 : 400 }}>
            {remaining != null ? `${remaining} free question${remaining !== 1 ? 's' : ''} remaining` : documentName}
          </span>
        </div>
        <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9E9189" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 10, minHeight: 120, maxHeight: 300 }}>
        {messages.length === 0 && !quotaExhausted && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '20px 0' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#B5AA9C" strokeWidth="1.5" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
            <span style={{ fontSize: 12, color: '#9E9189', textAlign: 'center' }}>
              {remaining != null
                ? `Ask up to ${remaining} question${remaining !== 1 ? 's' : ''} about this document.`
                : 'Ask a question about the document you\'re signing.'}
            </span>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              display: 'flex', flexDirection: 'column', gap: 4,
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
            }}
          >
            <div style={{
              padding: '8px 12px', borderRadius: 12,
              backgroundColor: msg.role === 'user' ? '#2E2318' : '#F5F2ED',
              color: msg.role === 'user' ? '#FDFCFB' : '#3D3229',
              fontSize: 13, lineHeight: '19px',
            }}>
              {msg.text}
            </div>
          </div>
        ))}

        {sending && (
          <div style={{ alignSelf: 'flex-start', padding: '8px 12px', borderRadius: 12, backgroundColor: '#F5F2ED' }}>
            <span style={{ fontSize: 12, color: '#9E9189' }}>Thinking...</span>
          </div>
        )}

        {error && (
          <div style={{ padding: '6px 10px', borderRadius: 8, backgroundColor: 'rgba(217,69,69,0.06)', border: '1px solid rgba(217,69,69,0.15)' }}>
            <span style={{ fontSize: 11, color: '#D94545' }}>{error}</span>
          </div>
        )}
      </div>

      {/* Quota exhausted */}
      {quotaExhausted && (
        <div style={{ padding: '14px 18px', borderTop: '1px solid #E8DED4', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#6B5E52', textAlign: 'center' }}>
            You've used your free questions for this document.
          </span>
          <button
            onClick={() => { window.open('/', '_blank'); }}
            style={{
              width: '100%', border: '1px solid #E8DED4', borderRadius: 8, padding: '9px 16px',
              backgroundColor: 'transparent', color: '#3D3229', fontSize: 12, fontWeight: 500,
              cursor: 'pointer', fontFamily: F,
            }}
          >
            Create a free Corner account to keep asking
          </button>
        </div>
      )}

      {/* Input */}
      {!quotaExhausted && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid #E8DED4', display: 'flex', gap: 8, flexShrink: 0 }}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
            placeholder="Ask about this document..."
            disabled={sending}
            maxLength={500}
            style={{
              flex: 1, border: '1px solid #E8DED4', borderRadius: 8,
              padding: '8px 12px', fontSize: 13, fontFamily: F,
              color: '#3D3229', backgroundColor: '#FAFAF8', outline: 'none',
            }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            style={{
              width: 36, height: 36, borderRadius: 8, border: 'none',
              backgroundColor: input.trim() && !sending ? '#D97645' : '#E8DED4',
              cursor: input.trim() && !sending ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ padding: '6px 18px 10px', flexShrink: 0 }}>
        <span style={{ fontSize: 10, color: '#B5AA9C', lineHeight: '14px' }}>
          AI explanation only — not legal advice. Consult an attorney for legal questions.
        </span>
      </div>
    </div>
  );
}
